import { noop } from 'lodash';
import { translate } from '../localization';

// Helper function to process elements for copying (hide, replace, format links/superscripts)
const processElementForCopy = (clonedElement: HTMLElement) => {
  const elementsToHide = Array.from(
    clonedElement.getElementsByClassName('escape-copy')
  );

  const elementsToReplace = Array.from(
    clonedElement.getElementsByClassName('replace-banner-text')
  );

  const superscriptButtonElements = Array.from(
    clonedElement.getElementsByClassName('superscript-button')
  );

  // Locate all anchor tags and update their content
  const anchorElements = Array.from(clonedElement.getElementsByTagName('a'));
  anchorElements.forEach(anchor => {
    const linkText = anchor.textContent || '';
    const linkHref = anchor.getAttribute('href') || '';
    const formattedText = `${linkText} (${linkHref})`;
    const textNode = document.createTextNode(formattedText);
    // Remove the newline node - let the formatting function handle line breaks
    anchor.replaceWith(textNode);
  });

  // Remove superscript buttons completely from the copied content
  superscriptButtonElements.forEach(element => {
    element.remove();
  });

  // Process bold elements that contain colons - only keep text after the colon
  const boldElements = Array.from(
    clonedElement.getElementsByTagName('b')
  ).concat(Array.from(clonedElement.getElementsByTagName('strong')));

  boldElements.forEach(boldElement => {
    const textContent = boldElement.textContent || '';
    const colonIndex = textContent.indexOf(':');

    if (colonIndex !== -1) {
      // Extract text after the colon and trim whitespace
      const textAfterColon = textContent.substring(colonIndex + 1).trim();

      if (textAfterColon) {
        // Replace the bold element with just the text after the colon
        const textNode = document.createTextNode(textAfterColon);
        boldElement.replaceWith(textNode);
      } else {
        // If no text after colon, remove the element entirely
        boldElement.remove();
      }
    }
  });

  // Hide elements with 'escape-copy' class
  elementsToHide.forEach(element => {
    const reference = element as HTMLElement;
    reference.style.display = 'none';
  });

  // Replace text content for elements with 'replace-banner-text' class
  elementsToReplace.forEach(element => {
    const reference = element as HTMLElement;
    reference.textContent = translate('not_selected_text');
  });
};

// Helper function to convert HTML to formatted plain text
const htmlToFormattedText = (element: HTMLElement): string => {
  let result = '';

  const BLOCK_ELEMENTS = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'];
  const BLOCK_WITH_BR = [...BLOCK_ELEMENTS, 'br'];
  const HEADING_ELEMENTS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

  const shouldSkipElement = (elementNode: HTMLElement): boolean => {
    const computedStyle = window.getComputedStyle(elementNode);
    if (computedStyle.display === 'none') return true;
    if (elementNode.classList.contains('escape-copy')) return true;
    return false;
  };

  const addLineBreakIfNeeded = (): void => {
    if (result && !result.endsWith('\n')) {
      result += '\n';
    }
  };

  const processTextNode = (text: string): void => {
    if (text === '\n') return; // Skip standalone newline nodes
    const cleanText = text.replace(/[ \t]+/g, ' ');
    result += cleanText;
  };

  const processElementNode = (
    elementNode: HTMLElement,
    tagName: string
  ): void => {
    if (shouldSkipElement(elementNode)) return;

    // Add line breaks before block elements
    if (BLOCK_ELEMENTS.includes(tagName)) {
      addLineBreakIfNeeded();
    }

    // Process child nodes
    elementNode.childNodes.forEach(processNode);

    // Add line breaks after block elements
    if (BLOCK_WITH_BR.includes(tagName) && !result.endsWith('\n')) {
      result += '\n';
    }

    // Add extra line break after headings for readability
    if (HEADING_ELEMENTS.includes(tagName)) {
      result += '\n';
    }
  };

  const processNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node.textContent || '');
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const elementNode = node as HTMLElement;
      const tagName = elementNode.tagName.toLowerCase();
      processElementNode(elementNode, tagName);
    }
  };

  element.childNodes.forEach(processNode);

  // Clean up multiple consecutive newlines and trim
  return result
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace 3+ newlines with 2
    .replace(/^\s+|\s+$/g, '') // Trim start and end
    .replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
};

export const copyToClipboard = (elementRef: HTMLElement, cb = noop) => {
  if (!elementRef) {
    cb(false);
    return;
  }

  const clonedRef = elementRef.cloneNode(true) as HTMLElement;

  // Process the cloned element (hide, replace, format elements)
  processElementForCopy(clonedRef);

  try {
    // Convert processed HTML to formatted plain text
    const formattedText = htmlToFormattedText(clonedRef);

    if (formattedText.trim()) {
      navigator.clipboard
        .writeText(formattedText)
        .then(() => {
          cb(true);
        })
        .catch(() => {
          cb(false);
        });
    } else {
      cb(false);
    }
  } catch {
    cb(false);
  }
};
export function copyAsTable(
  dataRowElementsOrElement: HTMLElement[] | HTMLElement,
  headerRowElement: HTMLElement,
  cb = noop
) {
  // Handle both single element and array cases
  const dataRowElements = Array.isArray(dataRowElementsOrElement)
    ? dataRowElementsOrElement
    : [dataRowElementsOrElement];

  if (!dataRowElements || dataRowElements.length === 0 || !headerRowElement) {
    cb(false);
    return;
  }

  // Create a new table element
  const table = document.createElement('table');
  table.style.borderCollapse = 'collapse'; // Collapses borders to eliminate double border lines

  // Define table cell style
  const cellStyle = 'border: 1px solid black; padding: 4px;';

  // Function to append a row to the table with styling and processing
  function appendRowToTable(rowElement: HTMLElement) {
    // Clone the row to avoid modifying the original
    const clonedRow = rowElement.cloneNode(true) as HTMLElement;

    // Apply the same processing logic as copyToClipboard
    processElementForCopy(clonedRow);

    const newRow = document.createElement('tr');
    Array.from(clonedRow.children).forEach(cell => {
      if (cell.getAttribute('role')) {
        const tableCell = document.createElement('td');
        tableCell.style.cssText = cellStyle; // Apply border and padding

        // Copy the processed HTML content of the cell
        tableCell.innerHTML = cell.innerHTML;

        // Add the cell to the table row
        newRow.appendChild(tableCell);
      }
    });
    table.appendChild(newRow);
  }

  // Append the header row to the table
  appendRowToTable(headerRowElement);

  // Append all data rows to the table
  dataRowElements.forEach(dataRowElement => {
    appendRowToTable(dataRowElement);
  });

  // Create an off-screen container for appending the table to the visible DOM
  const offScreenDiv = document.createElement('div');
  offScreenDiv.style.position = 'absolute';
  offScreenDiv.style.left = '-9999px';
  offScreenDiv.appendChild(table);
  document.body.appendChild(offScreenDiv);

  try {
    // Create a range and set its contents to the table
    const range = document.createRange();
    range.selectNodeContents(table);

    // Get the current selection
    const selection = window.getSelection();

    if (selection) {
      // Remove any existing selections
      selection.removeAllRanges();
      // Add the new range to the selection
      selection.addRange(range);

      // Execute the 'copy' command
      document.execCommand('copy');
    }
  } catch {
    cb(false);
  }

  // Clean up by removing the off-screen container
  document.body.removeChild(offScreenDiv);
  cb(true);
}
